import React, { useEffect, useRef, useState } from "react"
import { computeTextColor, DEFAULT_PDF_COLOR, isValidHexColor, PRESET_COLORS } from "./colorUtils"
import OfferPreview from "./OfferPreview"

interface Props {
    // Vide = couleur non définie (héritage)
    value: string
    onChange: (value: string) => void
    inheritedColor?: string
    inheritedFrom?: string
    // Couleur saisie dans le popover mais pas encore validée
    onPreviewChange?: (color: string | undefined) => void
    // Aperçu de l'offre dans le popover (inutile quand il est déjà affiché sur la page)
    showPreview?: boolean
}

// Hauteurs approximatives du popover, avec et sans l'aperçu de l'offre
const POPOVER_HEIGHT = 190
const POPOVER_WITH_PREVIEW_HEIGHT = 560
const SAVE_BANNER_HEIGHT = 80

export default function ColorPicker({ value, onChange, inheritedColor, inheritedFrom, onPreviewChange, showPreview = true }: Props) {
    const [ isOpen, setIsOpen ] = useState(false)
    const [ openUpward, setOpenUpward ] = useState(false)
    const [ draft, setDraft ] = useState('')
    const [ draftError, setDraftError ] = useState(false)
    const [ isPickerHovered, setIsPickerHovered ] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)

    const draftColor = `#${draft}`
    const shownColor = isOpen && isValidHexColor(draftColor) ? draftColor : value
    const isEmpty = shownColor === ''
    const previewColor = isOpen && isValidHexColor(draftColor) ? draftColor : undefined

    // Ref : évite de relancer l'effet quand le parent passe une fonction inline
    const onPreviewChangeRef = useRef(onPreviewChange)
    onPreviewChangeRef.current = onPreviewChange

    useEffect(() => {
        onPreviewChangeRef.current?.(previewColor)
    }, [ previewColor ])

    // Démontage popover ouvert (changement d'onglet) : ne pas laisser un aperçu non validé
    useEffect(() => () => onPreviewChangeRef.current?.(undefined), [])
    // L'input natif n'accepte qu'un #rrggbb minuscule
    const pickerColor = (isValidHexColor(draftColor) ? draftColor : (isEmpty ? inheritedColor ?? '#000000' : value)).toLowerCase()

    useEffect(() => {
        if (!isOpen) {
            return
        }

        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false)
            }
        }
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setIsOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleEscape)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('keydown', handleEscape)
        }
    }, [ isOpen ])

    const open = () => {
        // Ouverture vers le haut si la place manque en bas (bannière fixe comprise) et qu'il y en a plus en haut
        const rect = containerRef.current?.getBoundingClientRect()
        const spaceBelow = rect ? window.innerHeight - rect.bottom - SAVE_BANNER_HEIGHT : Infinity
        const spaceAbove = rect ? rect.top : 0
        const popoverHeight = showPreview ? POPOVER_WITH_PREVIEW_HEIGHT : POPOVER_HEIGHT
        setOpenUpward(spaceBelow < popoverHeight && spaceAbove > spaceBelow)
        setDraft(value === '' ? '' : value.substring(1))
        setDraftError(false)
        setIsOpen(true)
    }

    const select = (color: string) => {
        onChange(color.toUpperCase())
        setIsOpen(false)
    }

    // Avec l'aperçu, une couleur de base est seulement prévisualisée : il faut valider pour l'appliquer
    const selectPreset = (color: string) => {
        if (showPreview) {
            setDraft(color.substring(1))
            setDraftError(false)
        } else {
            select(color)
        }
    }

    const validateDraft = () => {
        if (isValidHexColor(draftColor)) {
            select(draftColor)
        } else {
            setDraftError(true)
        }
    }

    const sectionTitle = (title: string) => (
        <div className="tw-font-bold tw-uppercase tw-text-gray-600" style={{ fontSize: 11, letterSpacing: '.03em' }}>
            {title}
        </div>
    )

    return (
        <div ref={containerRef} className="tw-relative tw-inline-flex tw-flex-col tw-gap-1">
            <button
                type="button"
                onClick={() => isOpen ? setIsOpen(false) : open()}
                className="tw-flex tw-flex-row tw-items-center tw-gap-2 tw-bg-white tw-rounded tw-px-2 tw-py-1"
                style={{ border: '1px solid #ced4da', minWidth: 230, cursor: 'pointer' }}
            >
                <span
                    className="tw-flex tw-items-center tw-justify-center tw-rounded tw-font-bold tw-select-none"
                    title={isEmpty ? undefined : `Texte ${computeTextColor(shownColor) === '#FFFFFF' ? 'blanc' : 'noir'}`}
                    style={{
                        width: 26,
                        height: 22,
                        fontSize: 11,
                        backgroundColor: isEmpty ? 'transparent' : shownColor,
                        color: isEmpty ? '#999' : computeTextColor(shownColor),
                        border: isEmpty ? '1px dashed #999' : '1px solid rgba(0,0,0,.15)',
                    }}
                >
                    {isEmpty ? '+' : 'Aa'}
                </span>
                <span className={`tw-flex-1 tw-text-left ${isEmpty ? 'tw-text-gray-500' : ''}`}>
                    {isEmpty ? 'Sélectionner une couleur' : shownColor}
                </span>
                <span className="tw-text-gray-500" style={{ fontSize: 9 }}>▼</span>
            </button>

            {isEmpty && inheritedColor && (
                <small className="tw-text-gray-500">
                    Hérite de {inheritedColor.toUpperCase()}{inheritedFrom ? ` (${inheritedFrom})` : ''}
                </small>
            )}

            {isOpen && (
                <div
                    className="tw-absolute tw-left-0 tw-bg-white tw-rounded-lg tw-flex tw-flex-col tw-gap-2 tw-p-4"
                    style={{
                        ...(openUpward ? { bottom: '100%', marginBottom: 6 } : { top: 36 }),
                        width: showPreview ? 314 : 280,
                        zIndex: 1010,
                        border: '1px solid #e5e5e5',
                        boxShadow: '0 6px 20px rgba(0,0,0,.15)',
                    }}
                >
                    {sectionTitle('Couleur de base')}
                    <div className="tw-flex tw-flex-row tw-gap-2">
                        {PRESET_COLORS.map((preset) => {
                            const isSelected = !isEmpty && shownColor.toUpperCase() === preset.value
                            return (
                                <button
                                    key={preset.value}
                                    type="button"
                                    title={`${preset.label} ${preset.value}`}
                                    onClick={() => selectPreset(preset.value)}
                                    className="tw-rounded tw-p-0"
                                    style={{
                                        width: 32,
                                        height: 26,
                                        backgroundColor: preset.value,
                                        border: isSelected ? '2px solid #fff' : '1px solid rgba(0,0,0,.1)',
                                        boxShadow: isSelected ? `0 0 0 2px ${preset.value}` : 'none',
                                        cursor: 'pointer',
                                    }}
                                />
                            )
                        })}
                    </div>

                    {sectionTitle('Couleur personnalisée')}
                    <div className="tw-flex tw-flex-row tw-gap-2 tw-items-center">
                        <div className="input-group input-group-sm tw-flex-1">
                            <div className="input-group-prepend">
                                <span className="input-group-text">#</span>
                            </div>
                            <input
                                type="text"
                                autoFocus
                                className={`form-control ${draftError ? 'is-invalid' : ''}`}
                                placeholder="RRGGBB"
                                maxLength={6}
                                value={draft}
                                onChange={(e) => {
                                    setDraft(e.currentTarget.value.replace('#', '').trim().toUpperCase())
                                    setDraftError(false)
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault()
                                        validateDraft()
                                    }
                                }}
                            />
                        </div>
                        <label
                            title="Ouvrir le sélecteur de couleur"
                            className="tw-relative tw-flex tw-flex-col tw-items-center tw-justify-center tw-mb-0 tw-bg-white tw-rounded"
                            onMouseEnter={() => setIsPickerHovered(true)}
                            onMouseLeave={() => setIsPickerHovered(false)}
                            style={{
                                height: 'calc(1.5em + .5rem + 2px)',
                                width: 36,
                                lineHeight: 1,
                                border: '1px solid #ced4da',
                                color: '#495057',
                                opacity: isPickerHovered ? 0.6 : 1,
                                transition: 'opacity .15s ease-in-out',
                            }}
                        >
                            <i className="fa-solid fa-palette" />
                            <span
                                className="tw-rounded"
                                style={{
                                    display: 'block',
                                    width: 16,
                                    height: 4,
                                    marginTop: 3,
                                    backgroundColor: pickerColor,
                                    boxShadow: '0 0 0 1px rgba(0,0,0,.15)',
                                }}
                            />
                            <input
                                type="color"
                                aria-label="Sélecteur de couleur"
                                className="tw-absolute tw-inset-0 tw-w-full tw-h-full"
                                style={{ opacity: 0, cursor: 'pointer' }}
                                value={pickerColor}
                                onChange={(e) => {
                                    setDraft(e.currentTarget.value.substring(1).toUpperCase())
                                    setDraftError(false)
                                }}
                            />
                        </label>
                        <button type="button" className="btn btn-primary btn-sm" onClick={validateDraft}>
                            Valider
                        </button>
                    </div>
                    {draftError && (
                        <small className="tw-text-red-600">
                            Format invalide : 6 caractères hexadécimaux attendus (ex. 1D8773)
                        </small>
                    )}

                    {showPreview && (
                        <>
                            {sectionTitle(`Aperçu${previewColor && previewColor !== value ? ' (non validé)' : ''}`)}
                            <OfferPreview color={shownColor || inheritedColor || DEFAULT_PDF_COLOR} />
                        </>
                    )}
                </div>
            )}
        </div>
    )
}
